"use client";

import { AccessToken, AuthProvider } from "@pharaday/auth";
import { ContainerProvider, usePrevious } from "@pharaday/utils";
import type { Query } from "@tanstack/react-query";
import type { Container } from "inversify";
import { useSearchParams } from "next/navigation";
import { type PropsWithChildren, Suspense, useEffect, useLayoutEffect } from "react";

import { useDebounceResetQueryClient } from "../hooks";
import useNamespaceId from "./useNamespaceId";

export type NamespaceContainerProviderProps = PropsWithChildren<{
  container: Container;
  apiKey?: string;
  resetQueriesPredicate?: (query: Query) => boolean;
}>;

const installApiKeySession = (container: Container, apiKey: string | null | undefined) => {
  if (!apiKey || !container.isBound(AuthProvider)) {
    return;
  }

  void container.get(AuthProvider).updateSession({
    accessToken: new AccessToken(apiKey),
  });
};

/**
 * Reads the `apiKey` query param to seed the session. It renders nothing, so it sits behind its own
 * `<Suspense>` boundary and leaves the children it used to gate in the static shell.
 */
const ApiKeySessionSeed = ({ container, apiKey: externalApiKey }: { container: Container; apiKey?: string }) => {
  const searchParams = useSearchParams();

  useEffect(() => {
    installApiKeySession(container, externalApiKey ?? searchParams.get("apiKey"));
  }, [container, externalApiKey, searchParams]);

  return null;
};

const NamespaceContainerProvider = ({
  children,
  apiKey: externalApiKey,
  resetQueriesPredicate,
  container,
}: NamespaceContainerProviderProps) => {
  const { namespaceId } = useNamespaceId();

  const debounceResetQueryClient = useDebounceResetQueryClient({
    predicate: resetQueriesPredicate,
  });
  const prevNamespaceId = usePrevious(namespaceId);

  useEffect(() => {
    if (prevNamespaceId && namespaceId && prevNamespaceId !== namespaceId) {
      void debounceResetQueryClient();
    }
  }, [debounceResetQueryClient, namespaceId, prevNamespaceId]);

  // Layout effects flush before the passive subscribe that starts child queries.
  useLayoutEffect(() => {
    installApiKeySession(container, externalApiKey);
  }, [container, externalApiKey]);

  return (
    <ContainerProvider value={container}>
      <Suspense>
        <ApiKeySessionSeed container={container} apiKey={externalApiKey} />
      </Suspense>
      {children}
    </ContainerProvider>
  );
};

export { useContainer as useNamespaceContainer } from "@pharaday/utils";
export { NamespaceContainerProvider };
